
import React, { useCallback, useMemo, useState } from 'react';
import { Gantt, ViewMode } from 'gantt-task-react';
import "gantt-task-react/dist/index.css";
import { X } from 'lucide-react';
import { DIVISIONS } from '../constants/divisions';

// Export helpers for shared usage if needed
export const getPriorityColor = (p) => {
    switch (p) {
        case 'High': return '#dc3545';
        case 'Immediate': return '#fd7e14';
        case 'Low': return '#28a745';
        default: return '#6c757d';
    }
};

export const getDivisionColor = (div) => {
    if (!div) return '#94a3b8';
    const normalizedDiv = String(div).toUpperCase().trim();
    const division = DIVISIONS.find(d => 
        d.id.toUpperCase() === normalizedDiv || 
        d.name.toUpperCase() === normalizedDiv
    );
    return division ? division.color : '#94a3b8';
};

const GanttChart = ({ items = [], viewMode = ViewMode.Day, users = [], onTaskClick }) => {
    const [selectedTask, setSelectedTask] = useState(null);

    const getUserName = useCallback((userId) => {
        if (!userId) return 'Unknown';
        const u = users.find(u => u.sub === userId || u.Username === userId || u.UserId === userId);
        return u ? (u.Name || u.Username || 'User') : userId;
    }, [users]);

    const getPriorityScore = (p) => {
        switch (p) {
            case 'Immediate': return 4;
            case 'High': return 3;
            case 'Medium': return 2;
            case 'Normal': return 2;
            case 'Low': return 1;
            default: return 0;
        }
    };

    // Helper to safely parse dates
    const parseDate = (dateStr) => {
        if (!dateStr) return new Date();
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return new Date();

        // Strip time component to avoid timezone off-by-one rendering issues in Timeline
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    };

    const transformedTasks = useMemo(() => {
        if (!Array.isArray(items) || items.length === 0) return [];

        let flattenedTasks = [];

        items.forEach(task => {
            // Mapping for both Tasks and Events if mixed
            const startDate = parseDate(task.start || task.StartDate || task.StartDateTime || task.startDate);
            const endDate = parseDate(task.end || task.EndDate || task.EndDateTime || task.endDate);

            if (endDate <= startDate) {
                endDate.setDate(startDate.getDate() + 1);
            }

            const division = task.Division || task.division || 'Other';
            const priority = task.Priority || task.priority || 'Normal';
            const gradientId = `grad-${division}-${priority}`;

            const parentId = task.TaskID || task.EventID || task.id;

            // Subtasks/Subevents
            let children = task.Subtasks || task.subevents || [];
            // Filter out Completed/Cancelled
            children = children.filter(s => s.Status !== 'Completed' && s.Status !== 'Cancelled');
            const hasChildren = children.length > 0;

            const creatorName = getUserName(task.CreatedBy || task.createdBy);
            const fullTitle = `${task.Title || 'Untitled'}\nPriority: ${priority}\nCreated By: ${creatorName}\nEnd: ${endDate.toLocaleDateString()}`;

            flattenedTasks.push({
                start: startDate,
                end: endDate,
                name: task.Title || 'Untitled',
                id: parentId,
                type: hasChildren ? 'project' : 'task',
                progress: 0,
                isDisabled: true,
                styles: {
                    progressColor: 'rgba(255,255,255,0.3)',
                    progressSelectedColor: 'rgba(255,255,255,0.5)',
                    backgroundColor: `url(#${gradientId})`,
                    backgroundSelectedColor: `url(#${gradientId})`
                },
                project: division,
                hideChildren: false,
                // Custom Data
                priority: priority,
                division: division,
                createdBy: creatorName,
                fullTitle: fullTitle,
                subtasks: children, // storing children here for modal
                level: 0,
                originalItem: task
            });

            if (hasChildren) {
                children.forEach(sub => {
                    const subStart = parseDate(sub.StartDate || sub.startDateTime || startDate);
                    const subEnd = parseDate(sub.EndDate || sub.endDateTime || endDate);
                    if (subEnd <= subStart) subEnd.setDate(subStart.getDate() + 1);

                    flattenedTasks.push({
                        start: subStart,
                        end: subEnd,
                        name: sub.Title || sub.ActivityNameManual || sub.title || 'Untitled Sub-item',
                        id: sub.SubTaskID || sub.SubEventID || `${parentId}-${Math.random()}`,
                        type: 'task',
                        project: parentId,
                        progress: 0,
                        isDisabled: true,
                        styles: { backgroundColor: '#e2e8f0', progressColor: '#cbd5e1' },
                        priority: 'Normal',
                        level: 1
                    });
                });
            }
        });

        return flattenedTasks.sort((a, b) => {
            const scoreA = getPriorityScore(a.priority);
            const scoreB = getPriorityScore(b.priority);
            if (scoreB !== scoreA) return scoreB - scoreA;
            return a.start - b.start;
        });
    }, [getUserName, items]); // Re-run when items or users change

    const handleExpanderClick = (task) => {
        // Gantt component handles hiding internally if we pass the right props, 
        // but often we need to toggle 'hideChildren' in our data.
        // Since useMemo computes strictly from items, we might need state for expansion if we want interactive toggle.
        // However, standard Gantt usually manages this via 'hideChildren' prop on task.
        // For simplicity with useMemo, we'll skip interactive expand/collapse modification of source for now
        // OR we can make a local copy state.
        // But user just wants "Timeline". Let's assume expanded by default is fine or we ignore toggle for now to keep it simple as per "removing internal logic".
        // Actually, let's just let it be.
    };

    // Define Gradients for Legend/Bars
    const renderGradients = () => {
        const priorities = ['High', 'Normal', 'Low', 'Immediate'];

        const gradients = [];
        
        // Add all centralized divisions
        DIVISIONS.forEach(div => {
            priorities.forEach(prio => {
                const divColor = div.color;
                const prioColor = getPriorityColor(prio);
                // We use both ID and Name to cover legacy tasks that might have either
                const idById = `grad-${div.id}-${prio}`;
                const idByName = `grad-${div.name}-${prio}`;
                
                gradients.push(
                    <linearGradient key={idById} id={idById} x1="0" x2="0" y1="0" y2="1">
                        <stop offset="50%" stopColor={divColor} />
                        <stop offset="50%" stopColor={prioColor} />
                    </linearGradient>
                );
                
                if (div.id !== div.name) {
                    gradients.push(
                        <linearGradient key={idByName} id={idByName} x1="0" x2="0" y1="0" y2="1">
                            <stop offset="50%" stopColor={divColor} />
                            <stop offset="50%" stopColor={prioColor} />
                        </linearGradient>
                    );
                }
            });
        });

        // Add fallback for 'Other'
        priorities.forEach(prio => {
            const divColor = '#94a3b8';
            const prioColor = getPriorityColor(prio);
            const id = `grad-Other-${prio}`;
            gradients.push(
                <linearGradient key={id} id={id} x1="0" x2="0" y1="0" y2="1">
                    <stop offset="50%" stopColor={divColor} />
                    <stop offset="50%" stopColor={prioColor} />
                </linearGradient>
            );
        });

        return (
            <svg style={{ position: 'absolute', width: 0, height: 0 }} aria-hidden="true">
                <defs>{gradients}</defs>
            </svg>
        );
    };

    // Modal
    const TaskDetailsModal = () => {
        if (!selectedTask) return null;
        // Map back fields
        const t = selectedTask;
        return (
            <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
            }}>
                <div style={{
                    background: 'white', padding: '2rem', borderRadius: '12px', width: '90%', maxWidth: '500px',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                        <h3 style={{ margin: 0, fontSize: '1.25rem' }}>{t.name}</h3>
                        <button onClick={() => setSelectedTask(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem', fontSize: '0.9rem', color: '#555' }}>
                        <div><strong>Priority:</strong> {t.priority}</div>
                        <div><strong>Created By:</strong> {t.createdBy}</div>
                        <div><strong>Start:</strong> {t.start.toLocaleDateString()}</div>
                        <div><strong>End:</strong> {t.end.toLocaleDateString()}</div>
                        <div><strong>Division:</strong> {t.division}</div>
                    </div>

                    {t.subtasks && t.subtasks.length > 0 && (
                        <>
                            <h4 style={{ fontSize: '1rem', marginBottom: '0.5rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem' }}>Sub-items</h4>
                            <ul style={{ paddingLeft: '1.2rem', margin: 0 }}>
                                {t.subtasks.map((sub, i) => (
                                    <li key={i} style={{ marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                                        <div style={{ fontStyle: 'italic', fontWeight: 500 }}>{sub.ActivityNameManual || sub.ActivityType || sub.title || 'Sub-item'}</div>
                                        <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                            Due: {sub.EndDate || sub.endDate ? new Date(sub.EndDate || sub.endDate).toLocaleDateString() : 'N/A'} • Status: {sub.Status}
                                            {sub.AssigneeName && ` • Assigned To: ${sub.AssigneeName}`}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {renderGradients()}

            {/* No Header Controls Here - They are in Calendar.js now */}

            <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', background: 'white', position: 'relative' }}>
                {transformedTasks.length === 0 ? (
                    <div style={{ padding: '2rem', textAlign: 'center', color: '#666', fontSize: '0.9rem' }}>No items found for selection.</div>
                ) : (
                    <Gantt
                        tasks={transformedTasks}
                        viewMode={viewMode}
                        onExpanderClick={handleExpanderClick}
                        onSelect={(task) => {
                            if (onTaskClick) onTaskClick(task.originalItem);
                            else setSelectedTask(task);
                        }}
                        listCellWidth="0px"
                        columnWidth={viewMode === ViewMode.Month ? 150 : viewMode === ViewMode.Week ? 120 : 65}
                        barFill={70}
                        rowHeight={35}
                        fontSize="0.8rem"
                        TaskListHeader={({ headerHeight }) => <div style={{ height: headerHeight, width: 0 }} />}
                        TaskListTable={({ rowHeight }) => <div style={{ height: rowHeight, width: 0 }} />}
                    />
                )}
            </div>

            {/* Legend moved to parent (Calendar.js) */}

            <TaskDetailsModal />
        </div>
    );
};

export default GanttChart;
